import { FC, MouseEvent, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import cs from 'classnames';

import useWindowDimensions from 'hooks/useWindowDimensions';

import handleClickOnActiveElement from 'utils/handleClickOnActiveElement';

import { ReactComponent as ArrowDown } from 'assets/icons/arrow-down.svg';

import styles from './Collapse.module.scss';
import {
  COLLAPSE_LINE_HEIGHT,
  COLLAPSE_MAX_STRINGS_BEFORE_COLLAPSE
} from './constants';

interface CollapseProps {
  children?: React.ReactNode;
  className?: string;
  maxStringsBeforeCollapse?: number;
}

export const Collapse: FC<CollapseProps> = ({
  children = null,
  className,
  maxStringsBeforeCollapse = COLLAPSE_MAX_STRINGS_BEFORE_COLLAPSE
}: CollapseProps) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const contentRef = useRef<HTMLDivElement>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);

  const isCollapsed = shouldShowButton && !isExpanded;

  useLayoutEffect(() => {
    if (!contentRef.current) {
      return;
    }

    const element = contentRef.current;
    const computedStyle = window.getComputedStyle(element);
    const lineHeight =
      parseFloat(computedStyle.lineHeight) || COLLAPSE_LINE_HEIGHT;
    const maxHeight = lineHeight * maxStringsBeforeCollapse;

    setShouldShowButton(element.scrollHeight > maxHeight);

    if (!isExpanded) {
      element.style.maxHeight = `${maxHeight}px`;
    } else {
      element.style.maxHeight = 'none';
    }
  }, [children, isExpanded, width]);

  const handleClickExpand = (e: MouseEvent<HTMLElement>) => {
    handleClickOnActiveElement(e);
    setIsExpanded((isExpanded) => !isExpanded);
  };

  return (
    <section className={cs(styles.container, [className])}>
      <div
        ref={contentRef}
        className={`${styles.content} ${isCollapsed ? styles.collapsed : ''}`}
      >
        {children}
      </div>
      {shouldShowButton && (
        <button className={styles.expandButton} onClick={handleClickExpand}>
          {t(isExpanded ? 'collapse.showLess' : 'collapse.showMore')}
          <ArrowDown
            className={cs(styles.icon, {
              [styles.rotated]: isExpanded
            })}
          />
        </button>
      )}
    </section>
  );
};

export default Collapse;
